// Libraries
import { useBreakpoint } from '@chakra-ui/react';
import { useSession } from 'next-auth/react';
import { useMemo } from 'react';
// Components
import { SectionConfig } from '@molecules/index';
// Utils
import { ACCESS_TOKEN, AppPath, LinersAppPaths, NavigationAuthorization } from '@constants/index';
import { sectionFilter } from '@helpers/index';
import text from '@translations/en.json';
import { config } from '@utils/env';
// Types
import { NavigationIcons } from '@/src/utils/config';
import { Breakpoints } from '@globalTypes/index';
import { useAuthorization } from '@hooks/index';

export const useSections = () => {
  const breakpoint = useBreakpoint({ ssr: false }) as Breakpoints;
  const { data } = useSession();
  const token = data?.user?.accessToken ?? '';
  const { getAuthorization } = useAuthorization(NavigationAuthorization);
  const auth = getAuthorization(
    NavigationAuthorization.vesselProfilerNavigate,
    NavigationAuthorization.cargoPredictionNavigate,
    NavigationAuthorization.ciiEeoiNavigate,
    NavigationAuthorization.ciiFleetRatingsNavigate,
    NavigationAuthorization.ciiHomeNavigate,
    NavigationAuthorization.deploymentPlannerNavigate,
    NavigationAuthorization.scheduleRecoveryNavigate,
    NavigationAuthorization.ciiFleetPerformanceNavigate,
    NavigationAuthorization.vesselModelsNavigate,
    NavigationAuthorization.performanceSimulatorNavigate,
    NavigationAuthorization.foulingSimulatorNavigate,
    NavigationAuthorization.chatbotNavigate
  );

  const sections = useMemo(() => {
    const sections: SectionConfig[] = [
      {
        id: 'home',
        links: [
          {
            text: text.home,
            url: AppPath.HOME,
            icon: NavigationIcons.HOUSE,
            isAuthorized: auth.ciiHomeNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'compliance',
        title: text.compliance,
        links: [
          {
            text: text.cii_fleet_ratings,
            url: AppPath.FLEET_CII_RATINGS,
            icon: NavigationIcons.RATING,
            isAuthorized: auth.ciiFleetRatingsNavigate,
            breakPointBlackList: [],
          },
          {
            text: text.eeoi_fleet_scores,
            url: AppPath.FLEET_EEOI_SCORES,
            icon: NavigationIcons.EEOI,
            isAuthorized: auth.ciiEeoiNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'liner',
        title: 'DEPLOYMENT PLANNER',
        links: [
          {
            text: 'Deployment Plans',
            url: `${config.services.linersUi}${LinersAppPaths.DeploymentPlanning}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.CALENDAR,
            isAuthorized: auth.deploymentPlannerNavigate,
            breakPointBlackList: [],
          },

          {
            text: 'Deployment Schedules',
            url: `${config.services.linersUi}${LinersAppPaths.DeploymentPlanningSchedules}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.TIME_RECOVERY,
            isAuthorized: auth.deploymentPlannerNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'scheduleRecovery',
        title: 'SCHEDULE RECOVERY',
        links: [
          {
            text: 'Resolve New Delay',
            url: `${config.services.linersUi}${LinersAppPaths.ScheduleRecoveryNewDelay}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.TIME_RECOVERY,
            isAuthorized: auth.scheduleRecoveryNavigate,
            breakPointBlackList: [],
          },
          {
            text: 'Past Recoveries',
            url: `${config.services.linersUi}${LinersAppPaths.ScheduleRecovery}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.PAST_RECOVERY,
            isAuthorized: auth.scheduleRecoveryNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'cargoPrediction',
        title: 'CARGO PREDICTION',
        links: [
          {
            text: 'Cargo Prediction',
            url: `${config.services.linersUi}${LinersAppPaths.CargoPrediction}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.GRAPH,
            isAuthorized: auth.cargoPredictionNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'vesselModels',
        title: 'VESSEL MODELS',
        links: [
          {
            text: 'Model Request Form',
            url: `${config.services.linersUi}${LinersAppPaths.VesselModels}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.ENVELOPE,
            isAuthorized: auth.vesselModelsNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'vessel-profiler',
        title: 'VESSEL PROFILER',
        links: [
          {
            text: 'Vessel Profiler',
            url: `${config.services.linersUi}${LinersAppPaths.VesselProfiler}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.VESSEL_PROFILER,
            isAuthorized: auth.vesselProfilerNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'performanceAnalysis',
        title: text.performance_analysis,
        links: [
          {
            text: 'Performance Simulator',
            url: `${config.services.linersUi}${LinersAppPaths.PerformanceSimulator}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.GRAPH,
            isAuthorized: auth.performanceSimulatorNavigate,
            breakPointBlackList: [],
          },
          {
            text: 'Fouling Simulator',
            url: `${config.services.linersUi}${LinersAppPaths.FoulingSimulator}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.FOULING_SIMULATOR,
            isAuthorized: auth.foulingSimulatorNavigate,
            breakPointBlackList: [],
          },
          {
            text: text.fleet_performance,
            url: AppPath.FLEET_PERFORMANCE_ANALYSIS,
            icon: NavigationIcons.FLEET_PERFORMANCE,
            isAuthorized: auth.ciiFleetPerformanceNavigate,
            breakPointBlackList: [],
          },
        ],
      },
      {
        id: 'ai-chatbot',
        title: 'AI CHATBOT',
        links: [
          {
            text: 'AI Chatbot',
            url: `${config.services.linersUi}${LinersAppPaths.AiChatbot}?${ACCESS_TOKEN}=${token}`,
            icon: NavigationIcons.AI_CHATBOT,
            isAuthorized: auth.chatbotNavigate,
            breakPointBlackList: [],
          },
        ],
      },
    ];

    return sectionFilter(sections, breakpoint);
  }, [breakpoint, auth, token]);

  return { sections };
};
